import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Calendar, Plus } from 'lucide-react';
import { formatDate } from '@/utils/format';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';

// Mock recycling items data
const mockRecycleItems = [
  {
    id: "REC-BILLY001",
    customer: {
      name: "John Smith",
      email: "john.smith@email.com"
    },
    product: {
      id: "BILLY-80263832",
      name: "BILLY Bookcase",
      category: "Storage"
    },
    status: "processing",
    condition: "good",
    receivedDate: "2024-03-15",
    estimatedCompletion: "2024-03-20",
    notes: "Minor wear and tear, suitable for recycling"
  },
  {
    id: "REC-POANG002",
    customer: {
      name: "Emma Johnson",
      email: "emma.j@email.com"
    },
    product: {
      id: "POANG-89129308",
      name: "POÄNG Armchair",
      category: "Seating"
    },
    status: "pending",
    condition: "fair",
    receivedDate: "2024-03-14",
    estimatedCompletion: "2024-03-19",
    notes: "Fabric needs assessment"
  },
  {
    id: "REC-MALM003",
    customer: {
      name: "Michael Brown",
      email: "m.brown@email.com"
    },
    product: {
      id: "MALM-89129307",
      name: "MALM Bed Frame",
      category: "Bedroom"
    },
    status: "completed",
    condition: "poor",
    receivedDate: "2024-03-13",
    estimatedCompletion: "2024-03-18",
    notes: "Recycled for materials"
  }
];

const dateRanges = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 180 days' },
  { value: '365d', label: 'Last 365 days' },
];

const recycleStatuses = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

const columns = [
  {
    accessorKey: 'id',
    header: 'Item ID',
    cell: ({ row }) => (
      <Link
        to={`/admin/re-commerce/recycle/${row.original.id}`}
        className="font-medium text-primary hover:underline"
      >
        {row.original.id}
      </Link>
    ),
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.customer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.customer.email}</div>
      </div>
    ),
  },
  {
    accessorKey: 'product',
    header: 'Product',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.product.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.product.category}</div>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status.toLowerCase();
      const variant = {
        pending: 'outline',
        processing: 'secondary',
        completed: 'default',
        rejected: 'destructive',
      }[status] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.status.replace('_', ' ').toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'condition',
    header: 'Condition',
    cell: ({ row }) => {
      const condition = row.original.condition.toLowerCase();
      const variant = {
        excellent: 'default',
        good: 'secondary',
        fair: 'outline',
        poor: 'destructive'
      }[condition] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.condition.toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'receivedDate',
    header: 'Received Date',
    cell: ({ row }) => formatDate(row.original.receivedDate),
  },
  {
    accessorKey: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>View Details</DropdownMenuItem>
          <DropdownMenuItem>Update Status</DropdownMenuItem>
          <DropdownMenuItem>Process Item</DropdownMenuItem>
          <DropdownMenuItem>Contact Customer</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export function RecycleItemsPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    dateRange: '30d',
  });

  // Filter items based on current filters
  const filteredItems = React.useMemo(() => {
    return mockRecycleItems.filter(item => {
      const matchesSearch =
        item.id.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.customer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.product.name.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === 'all' ||
        item.status === filters.status;

      return matchesSearch && matchesStatus;
    });
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Recycling Items</h1>
          <p className="text-sm text-muted-foreground">
            Manage and track recycling items
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Item
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search items..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {recycleStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => handleFilterChange('dateRange', value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    {dateRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={filteredItems}
            emptyMessage="No recycling items found"
            loadingMessage="Loading recycling items..."
          />
        </CardContent>
      </Card>
    </div>
  );
} 
